import axios from 'axios';
import { createApiRequest } from 'core/util';
import { constructApiUrl } from 'core/util/api';
import store, { AppThunk, AppDispatch } from 'core/store';
import {
  DOCUMENTS_LIST_URL,
  DOCUMENTS_SEARCH_URL,
  DOCUMENTS_URL,
  DOCUMENT_COUNT_URL,
  DOCUMENT_TYPES_URL,
  DOCUMENT_URL
} from 'core/constants';
import { isFileTypeVideo, isImageFileUrl } from 'core/util';
import {
  fetchDocumentCountSuccess,
  fetchDocumentCountError,
  fetchDocumentsSuccess,
  fetchDocumentsError,
  createDocumentStart,
  createDocumentSuccess,
  createDocumentError,
  fetchDocumentCountStart,
  fetchDocumentsStart,
  fetchDocumentTypesSuccess,
  fetchDocumentTypesError,
  fetchSearchedDocumentsStart,
  fetchSearchedDocumentsSuccess,
  fetchSearchedDocumentsError,
  deleteDocumentError,
  deleteDocumentStart,
  deleteDocumentSuccess,
  updateDocumentError,
  updateDocumentStart,
  updateDocumentSuccess,
  setConstructionPhotos
} from 'features/shared/documents/DocumentsSlice';

export const fetchDocumentTypes = () =>
  createApiRequest({
    route: DOCUMENT_TYPES_URL,
    onSuccess: fetchDocumentTypesSuccess,
    onError: fetchDocumentTypesError,
    defaultErrorMessage: 'There was a problem loading your documents.',
    useGlobalLoader: true
  })();

export const fetchDocumentCount = (homeId: number) =>
  createApiRequest({
    route: DOCUMENT_COUNT_URL,
    replace: ['home_id', homeId],
    onStart: fetchDocumentCountStart,
    onSuccess: fetchDocumentCountSuccess,
    onError: fetchDocumentCountError,
    defaultErrorMessage: 'There was a problem loading your documents.',
    useGlobalLoader: true
  })();

export const fetchAllDocuments = (homeId: number, type: string) =>
  createApiRequest({
    route: DOCUMENTS_LIST_URL,
    replace: [
      ['home_id', homeId],
      ['type', type]
    ],
    onStart: fetchDocumentsStart,
    onSuccess: fetchDocumentsSuccess,
    onError: fetchDocumentsError,
    defaultErrorMessage: 'There was a problem loading your documents.',
    useGlobalLoader: true,
    paginate: true
  })();

/** Fetches construction document images for Construction Progress card preview.
 * Fetches from all construction types (construction, construction_*, etc.) and
 * aggregates items where type_label includes 'construction'.
 */
export const fetchConstructionProgressImages =
  (homeId: number, constructionTypes: string[]): AppThunk =>
  async (dispatch: AppDispatch) => {
    if (!constructionTypes.length) {
      dispatch(setConstructionPhotos({ items: [] }));
      return;
    }
    try {
      const token = store.getState().login.accessToken;
      const allItems: any[] = [];
      await Promise.all(
        constructionTypes.map(async (type) => {
          const url = constructApiUrl({
            route: DOCUMENTS_LIST_URL,
            replace: [
              ['home_id', homeId],
              ['type', type]
            ]
          });
          const res = await axios({
            method: 'get',
            params: { page: 1 },
            url,
            headers: { Authorization: token ? `Bearer ${token}` : undefined }
          });
          const items = res.data?.results?.items ?? [];
          allItems.push(...items);
        })
      );
      const filtered = allItems.filter(
        (item) =>
          (item.type_label ?? '').toLowerCase().includes('construction') &&
          (item.thumbnail_url ||
            (item.file_url &&
              (isImageFileUrl(item.file_url) ||
                isFileTypeVideo(item.file_url))))
      );
      dispatch(setConstructionPhotos({ items: filtered }));
    } catch {
      dispatch(setConstructionPhotos({ items: [] }));
    }
  };

export const fetchSearchedDocuments = (homeId: number, term: string) =>
  createApiRequest({
    route: DOCUMENTS_SEARCH_URL,
    method: 'get',
    replace: [
      ['home_id', homeId],
      ['term', term]
    ],
    onStart: fetchSearchedDocumentsStart,
    onSuccess: fetchSearchedDocumentsSuccess,
    onError: fetchSearchedDocumentsError,
    defaultErrorMessage: 'There was a problem searching documents.'
  })();

export const createDocument = (homeId: number, body: object) =>
  createApiRequest({
    route: DOCUMENTS_URL,
    method: 'post',
    body: body,
    replace: [['home_id', homeId]],
    onStart: createDocumentStart,
    onSuccess: createDocumentSuccess,
    onError: createDocumentError,
    defaultErrorMessage: 'There was an error updating the product templates.'
  })();

export const updateDocument = (homeId: number, assetId: number, body: object) =>
  createApiRequest({
    route: DOCUMENT_URL,
    method: 'patch',
    body: body,
    replace: [
      ['home_id', homeId],
      ['asset_id', assetId]
    ],
    onStart: updateDocumentStart,
    onSuccess: updateDocumentSuccess,
    onError: updateDocumentError,
    defaultErrorMessage: 'There was an error updating the document.'
  })();

export const deleteDocument = (homeId: number, assetId: number) =>
  createApiRequest({
    route: DOCUMENT_URL,
    method: 'delete',
    replace: [
      ['home_id', homeId],
      ['asset_id', assetId]
    ],
    onStart: deleteDocumentStart,
    onSuccess: deleteDocumentSuccess,
    onError: deleteDocumentError,
    defaultErrorMessage: 'There was a problem deleting the document.',
    useGlobalLoader: true
  })();
